import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { inboxMessageParts } from "@wmf/shared/inboxLinks";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isOwner, isStaff, useAuth } from "../auth.js";
import { formatDate, formatTime } from "../format.js";
import { Pagination } from "../Pagination.js";
import { useAsync } from "../useAsync.js";
import { useInboxReplyDrafts } from "./drafts.js";
import {
  inboxApi,
  type InboxConversation,
  type InboxFilter,
  type InboxMessage,
} from "./api.js";
import "./inbox.css";
import { useInboxUnread } from "./unread.js";
import { useNotifications } from "../notifications/Bell.js";
import { ReplyEditor } from "./ReplyEditor.js";
import { MessageAlerts } from "../push/MessageAlerts.js";
import { useLiveShellRefresh } from "../liveRefresh.js";
import type { InboxThreadView } from "./api.js";

/**
 * One Inbox, two shells.
 *
 * The list and the open conversation are two routes rather than two panes with
 * a piece of state deciding which is showing, because that is what makes the
 * mobile behaviour fall out for free: `/inbox` is the list, `/inbox/<id>` is a
 * conversation, Back is the browser's own, and a conversation has an address
 * somebody can be sent to. On a wide screen the stylesheet puts them side by
 * side; on a narrow one it shows whichever the route selected. No JavaScript
 * measures a viewport to decide.
 */

function whenLabel(iso: string): string {
  const at = new Date(iso);
  const today = new Date();
  const sameDay =
    at.getFullYear() === today.getFullYear() &&
    at.getMonth() === today.getMonth() &&
    at.getDate() === today.getDate();
  return sameDay ? formatTime(iso) : formatDate(iso);
}

function inboxFilter(value: string | null): InboxFilter {
  return value === "unread" ? "unread" : "all";
}

function inboxPage(value: string | null): number {
  if (value === null) return 1;
  const page = Number(value);
  return Number.isSafeInteger(page) && page >= 1 ? page : 1;
}

function inboxListParams(filter: InboxFilter, page: number): URLSearchParams {
  const params = new URLSearchParams();
  if (filter === "unread") params.set("filter", filter);
  if (page > 1) params.set("page", String(page));
  return params;
}

function inboxListSearch(filter: InboxFilter, page: number): string {
  const query = inboxListParams(filter, page).toString();
  return query === "" ? "" : `?${query}`;
}

function accountIdFromHistoryState(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const accountId = (state as { inboxAccountId?: unknown }).inboxAccountId;
  return typeof accountId === "string" ? accountId : null;
}

function lessonIdFromHistoryState(state: unknown, accountId: string): string | null {
  if (accountIdFromHistoryState(state) !== accountId || typeof state !== "object" || state === null) return null;
  const lessonId = (state as { inboxLessonId?: unknown }).inboxLessonId;
  return typeof lessonId === "string" && lessonId.length > 0 ? lessonId : null;
}

function ConversationRow({
  conversation,
  selected,
  search,
  accountId,
}: {
  conversation: InboxConversation;
  selected: boolean;
  search: string;
  accountId: string;
}) {
  const unread = conversation.unreadCount > 0;
  return (
    <li>
      <Link
        className={`inbox-row${selected ? " is-selected" : ""}${unread ? " is-unread" : ""}`}
        to={{ pathname: `/inbox/${encodeURIComponent(conversation.id)}`, search }}
        state={{ inboxAccountId: accountId }}
        aria-current={selected ? "page" : undefined}
      >
        <span className="inbox-row-head">
          <strong className="inbox-row-title">
            {unread && <span aria-label="Unread">● </span>}
            {conversation.title}
          </strong>
          <time className="small muted" dateTime={conversation.lastMessageAt}>
            {whenLabel(conversation.lastMessageAt)}
          </time>
        </span>
        {conversation.participants && (
          <span className="small muted inbox-row-people">{conversation.participants}</span>
        )}
        {conversation.latest && (
          <span className="inbox-row-latest">
            <span className="inbox-row-subject">
              {conversation.latest.subject}
              {conversation.hasUnreadImportant && (
                <span className="tag warn inbox-important">Important</span>
              )}
            </span>
            <span className="small muted inbox-row-preview">{conversation.latest.preview}</span>
          </span>
        )}
      </Link>
    </li>
  );
}

/**
 * Starting a conversation with a colleague.
 *
 * The picker is the whole of the recipient selection: names come from the
 * server's active-staff directory and travel back as account ids, so there is
 * no box an address could be typed into and nobody who has left can be chosen.
 */
function NewStaffConversation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const directory = useAsync(() => (open ? inboxApi.staff() : Promise.resolve(null)), [open]);

  async function start(accountId: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { threadId } = await inboxApi.openStaffConversation(accountId);
      setOpen(false);
      navigate(`/inbox/${encodeURIComponent(threadId)}${location.search}`, {
        state: { inboxAccountId: me?.user.id ?? null },
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "We could not start that conversation.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="inbox-new">
      <button
        type="button"
        className="small"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? "Cancel" : "Message a colleague"}
      </button>
      {open && (
        <div className="panel inbox-staff-picker">
          {directory.loading && <p className="small muted">Loading colleagues…</p>}
          {directory.error && <div className="error">We could not load the staff list.</div>}
          {directory.data?.staff.length === 0 && (
            <p className="small muted">There is nobody else to message yet.</p>
          )}
          {error && <div className="error">{error}</div>}
          {directory.data?.staff.map((person) => (
            <button
              key={person.accountId}
              type="button"
              className="quiet inbox-staff-option"
              disabled={busy}
              onClick={() => void start(person.accountId)}
            >
              <strong>{person.name}</strong>
              <span className="small muted">{person.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConversationList({
  selectedId,
  filter,
  onFilter,
  page,
  onPage,
  nonce,
  search,
  accountId,
}: {
  selectedId: string | null;
  filter: InboxFilter;
  onFilter: (next: InboxFilter) => void;
  page: number;
  onPage: (next: number, options?: { replace?: boolean }) => void;
  search: string;
  accountId: string;
  /**
   * Bumped by whatever changes what this list says.
   *
   * Reading a conversation and replying in one both change the row beside it —
   * its unread mark, its preview, its position, and whether it belongs in the
   * Unread filter at all. Without this the list is fetched once and keeps
   * showing the state before the thing the person just did.
   */
  nonce: number;
}) {
  const { me } = useAuth();
  const conversations = useAsync(
    async () => ({
      filter,
      page,
      accountId,
      result: await inboxApi.conversations(filter, page),
    }),
    [filter, page, nonce, accountId],
  );
  const data = conversations.data?.filter === filter
    && conversations.data.page === page
    && conversations.data.accountId === accountId
    ? conversations.data.result
    : null;
  // While another page of the same account and filter loads, keep showing the
  // rows (and the pager) already on screen so keyboard focus is not lost to
  // body. Rows from another filter or account are never carried over.
  const shown = data
    ?? (conversations.data?.filter === filter && conversations.data.accountId === accountId
      ? conversations.data.result
      : null);

  useEffect(() => {
    if (!data) return;
    const pages = Number.isSafeInteger(data.pages) && data.pages > 0 ? data.pages : 1;
    // A stale page is corrected in place; it must not add a history entry
    // that Back would immediately clamp (and push) again.
    if (page > pages) onPage(pages, { replace: true });
  }, [data, onPage, page]);

  return (
    <div className="inbox-list" aria-label="Conversations">
      {isOwner(me) && (
        <div className="inbox-new">
          <Link className="notes-link" to="/inbox/announcements">
            Announcements
          </Link>
        </div>
      )}
      {isStaff(me) && <NewStaffConversation />}
      <div className="row inbox-filters">
        <button
          type="button"
          className={filter === "all" ? "small" : "quiet small"}
          aria-pressed={filter === "all"}
          onClick={() => onFilter("all")}
        >
          All
        </button>
        <button
          type="button"
          className={filter === "unread" ? "small" : "quiet small"}
          aria-pressed={filter === "unread"}
          onClick={() => onFilter("unread")}
        >
          Unread
        </button>
      </div>

      {conversations.loading && !shown && <p className="empty">Loading your Inbox…</p>}
      {conversations.error && !data && (
        <div className="error">We could not load your Inbox right now.</div>
      )}
      {shown && shown.items.length === 0 && (
        <p className="empty">
          {filter === "unread" ? "Nothing unread." : "No conversations yet."}
        </p>
      )}

      <ul className="inbox-rows">
        {shown?.items.map((conversation) => (
          <ConversationRow
            key={conversation.id}
            conversation={conversation}
            selected={conversation.id === selectedId}
            search={search}
            accountId={accountId}
          />
        ))}
      </ul>

      {shown && shown.pages > 1 && (
        <Pagination
          page={shown.page}
          pages={shown.pages}
          ariaLabel="Inbox pagination"
          onPageChange={onPage}
        />
      )}
    </div>
  );
}

function Message({ message }: { message: InboxMessage }) {
  return (
    <article className={`inbox-message${message.mine ? " is-mine" : ""}`}>
      <header className="inbox-message-head">
        <strong>{message.mine ? "You" : message.senderName}</strong>
        <time className="small muted" dateTime={message.publishedAt}>
          {formatDate(message.publishedAt)} at {formatTime(message.publishedAt)}
        </time>
      </header>
      {message.subject && <div className="inbox-message-subject small">{message.subject}</div>}
      {message.contextHref && <p className="small muted inbox-message-context"><Link to={message.contextHref}>Open related lesson</Link></p>}
      {message.importance === "important" && <span className="tag warn">Important</span>}
      <div className="notes inbox-message-body">
        {message.body.split("\n").map((line, index) => (
          <p key={index}>{line ? inboxMessageParts(line).map((part, partIndex) => part.href
            ? <a key={partIndex} href={part.href} target="_blank" rel="noopener noreferrer">{part.text}</a>
            : part.text) : " "}</p>
        ))}
      </div>
    </article>
  );
}

function ConversationHistory({ threadId, latest, onAtBottom, sentVersion }: {
  threadId: string; latest: InboxThreadView["messages"]; onAtBottom: (value: boolean) => void; sentVersion: number;
}) {
  const pane = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);
  const anchor = useRef<{ height: number; top: number } | null>(null);
  const mounted = useRef(true);
  const [stored, setStored] = useState<InboxMessage[]>([]);
  const [older, setOlder] = useState<{ cursor: string | null; page: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [below, setBelow] = useState(false);
  const [historyLive, setHistoryLive] = useState(true);
  useEffect(() => {
    if (busy || historyLive) return;
    // Restore announcements after the older messages have committed with live=off.
    const frame = requestAnimationFrame(() => setHistoryLive(true));
    return () => cancelAnimationFrame(frame);
  }, [busy, historyLive]);
  const previousNewest = useRef<string | null>(null);
  useLayoutEffect(() => { nearBottom.current = true; if (pane.current) pane.current.scrollTop = pane.current.scrollHeight; setBelow(false); }, [sentVersion]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useLayoutEffect(() => {
    const element = pane.current; if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => { if (nearBottom.current) element.scrollTop = element.scrollHeight; });
    observer.observe(element); return () => observer.disconnect();
  }, []);
  useEffect(() => { setStored(previous => [...new Map([...previous, ...latest.items].map(message => [message.id, message])).values()]); }, [latest]);
  const messages = [...new Map([...stored, ...latest.items].map(message => [message.id, message])).values()]
    .sort((a, b) => a.publishedAt.localeCompare(b.publishedAt) || a.id.localeCompare(b.id));
  const newest = latest.items[0]?.id ?? null;
  const cursor = older ? older.cursor : latest.nextCursor ?? null;
  const canLoad = older ? older.cursor !== null : latest.nextCursor !== undefined ? latest.nextCursor !== null : latest.pages > 1;
  useLayoutEffect(() => {
    const element = pane.current; if (!element) return;
    if (anchor.current) {
      element.scrollTop = anchor.current.top + element.scrollHeight - anchor.current.height; anchor.current = null;
    } else if (nearBottom.current) {
      element.scrollTop = element.scrollHeight; setBelow(false);
    } else if (newest !== previousNewest.current) setBelow(true);
    previousNewest.current = newest;
  }, [messages.map(message => message.id).join(":"), newest]);
  async function loadEarlier() {
    if (busy || !canLoad) return;
    setBusy(true); setHistoryLive(false); setError(null);
    try {
      const page = older ? older.page + 1 : 2;
      const result = cursor ? await inboxApi.earlier(threadId, cursor) : await inboxApi.thread(threadId, page);
      if (!mounted.current) return;
      if (pane.current) anchor.current = { height: pane.current.scrollHeight, top: pane.current.scrollTop };
      nearBottom.current = false; onAtBottom(false);
      setStored(previous => [...new Map([...previous, ...result.messages.items].map(message => [message.id, message])).values()]);
      setOlder({ cursor: result.messages.nextCursor ?? null, page });
    } catch { if (mounted.current) setError("We couldn't load earlier messages. Try again."); }
    finally { if (mounted.current) setBusy(false); }
  }
  return <>
    <div ref={pane} className="inbox-messages inbox-chat-history" role="log" aria-label="Conversation history" aria-live={historyLive ? "polite" : "off"} aria-busy={busy || !historyLive} aria-relevant="additions" tabIndex={0}
      onScroll={() => { const element = pane.current!; const atBottom = element.scrollHeight - element.clientHeight - element.scrollTop < 60;
        nearBottom.current = atBottom; onAtBottom(atBottom); if (atBottom) setBelow(false); }}>
      {canLoad && <button type="button" className="quiet inbox-load-earlier" disabled={busy} onClick={() => void loadEarlier()}>{busy ? "Loading…" : "Load earlier messages"}</button>}
      {error && <p className="error" role="alert">{error}</p>}
      {messages.length === 0 && <p className="empty">Nothing here yet.</p>}
      {messages.map(message => <Message key={message.id} message={message} />)}
    </div>
    {below && <button type="button" className="quiet inbox-jump-latest" onClick={() => {
      if (pane.current) pane.current.scrollTop = pane.current.scrollHeight;
      nearBottom.current = true; onAtBottom(true); setBelow(false);
    }}>New messages ↓</button>}
  </>;
}

function newReplyAttemptId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2);
}

type ReplyAttempt = { fingerprint: string; requestId: string };

function Reply({ threadId, onSent, linkPicker, linkAudienceLabel, lessonContextId }: { threadId: string; onSent: () => void; linkPicker: boolean; linkAudienceLabel: string; lessonContextId: string | null }) {
  const drafts = useInboxReplyDrafts();
  const editorId = useId();
  const initialDraft = drafts.get(threadId);
  const [body, setBody] = useState(() => initialDraft?.body ?? "");
  const [important, setImportant] = useState(() => initialDraft?.important ?? false);
  const [linkedLessonId, setLinkedLessonId] = useState(() =>
    initialDraft?.lessonContextId === lessonContextId
      ? initialDraft.lessonId ?? null
      : lessonContextId,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const requestAttempt = useRef<ReplyAttempt | null>(null);
  const requestContext = useRef({ threadId, lessonContextId });
  if (!requestAttempt.current && initialDraft?.requestId && initialDraft.requestFingerprint) {
    requestAttempt.current = {
      requestId: initialDraft.requestId,
      fingerprint: initialDraft.requestFingerprint,
    };
  }
  useEffect(() => {
    if (requestContext.current.threadId !== threadId || requestContext.current.lessonContextId !== lessonContextId) {
      setLinkedLessonId(lessonContextId);
      requestAttempt.current = null;
      requestContext.current = { threadId, lessonContextId };
    }
  }, [threadId, lessonContextId]);
  function updateBody(nextBody: string) {
    setBody(nextBody);
    drafts.save(threadId, { body: nextBody, important, lessonId: linkedLessonId, lessonContextId });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const message: {
        body: string;
        importance: "normal" | "important";
        lessonId?: string;
      } = {
        body,
        importance: important ? "important" : "normal",
        ...(linkedLessonId ? { lessonId: linkedLessonId } : {}),
      };
      const fingerprint = JSON.stringify(message);
      let attempt = requestAttempt.current;
      if (!attempt || attempt.fingerprint !== fingerprint) {
        attempt = { requestId: newReplyAttemptId(), fingerprint };
        requestAttempt.current = attempt;
      }
      drafts.save(threadId, {
        body,
        important,
        lessonId: linkedLessonId,
        lessonContextId,
        requestId: attempt.requestId,
        requestFingerprint: fingerprint,
      });
      const sent = await inboxApi.reply(threadId, attempt.requestId, message);
      if (sent.recipientCount === 0) {
        throw new Error("Nobody is currently available to receive this message.");
      }
      setBody("");
      setImportant(false);
      setLinkedLessonId(null);
      requestAttempt.current = null;
      drafts.clear(threadId);
      onSent();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "We could not send that message.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <form className="form-stack inbox-reply" onSubmit={(event) => void submit(event)}>
      <div className="field-stack">
        <label htmlFor={editorId} className="small muted">Reply</label>
        {linkPicker ? <ReplyEditor id={editorId} threadId={threadId} value={body} onChange={updateBody} disabled={busy} audienceLabel={linkAudienceLabel} /> : <textarea
          id={editorId}
          value={body}
          maxLength={5000}
          rows={2}
          required
          onChange={(event) => updateBody(event.target.value)}
          disabled={busy}
        />}
      </div>
      {linkedLessonId && <p className="small muted inbox-message-context">The next reply will link to this published lesson note. <button type="button" className="quiet" disabled={busy} onClick={() => {
        setLinkedLessonId(null);
        requestAttempt.current = null;
        drafts.save(threadId, { body, important, lessonId: null, lessonContextId });
      }}>Remove lesson link</button></p>}
      <div className="row inbox-reply-actions">
        <label className="row small">
          <input
            type="checkbox"
            checked={important}
            onChange={(event) => {
              const nextImportant = event.target.checked;
              setImportant(nextImportant);
              drafts.save(threadId, { body, important: nextImportant, lessonId: linkedLessonId, lessonContextId });
            }}
            disabled={busy}
          />
          Mark as important
        </label>
        <button type="submit" disabled={busy || body.trim() === ""}>
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
      {error && <div className="error">{error}</div>}
    </form>
  );
}

function Conversation({
  threadId,
  onChanged,
  listSearch,
  accountId,
  lessonContextId,
}: {
  threadId: string;
  listSearch: string;
  accountId: string;
  lessonContextId: string | null;
  /** Reading or replying changes the list beside this one. */
  onChanged: () => void;
}) {
  const { reload: reloadUnread } = useInboxUnread();
  const { reload: reloadNotifications } = useNotifications();
  const [nonce, setNonce] = useState(0);
  const container = useRef<HTMLDivElement>(null);
  const [chatHeight, setChatHeight] = useState<number>();
  const [atBottom, setAtBottom] = useState(true);
  const navigationKey = useLocation().key;
  const view = useAsync(async () => ({
    ...await inboxApi.thread(threadId, 1),
    navigationKey,
    accountId,
  }), [threadId, nonce, navigationKey, accountId]);
  useLiveShellRefresh(view.reload);
  const readKey = `${threadId}:${navigationKey}:${view.data?.messages.items[0]?.id ?? ""}`;
  const markedFor = useRef<string | null>(null);
  useEffect(() => { setAtBottom(true); }, [navigationKey]);

  useLayoutEffect(() => {
    const element = container.current; if (!element) return;
    const resize = () => { const viewport = window.visualViewport; const bottom = (viewport?.height ?? window.innerHeight) + (viewport?.offsetTop ?? 0);
      setChatHeight(Math.max(240, Math.floor(bottom - Math.max(0, element.getBoundingClientRect().top) - 16))); };
    resize(); window.addEventListener("resize", resize); window.visualViewport?.addEventListener("resize", resize); window.visualViewport?.addEventListener("scroll", resize);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize); observer?.observe(element.closest(".inbox") ?? element);
    return () => { window.removeEventListener("resize", resize); window.visualViewport?.removeEventListener("resize", resize); window.visualViewport?.removeEventListener("scroll", resize); observer?.disconnect(); };
  }, [view.data?.navigationKey, accountId]);

  // Acknowledge the conversation and all its notifications once per visit or
  // new message, including alerts left over from an earlier message read.
  useEffect(() => {
    if (!atBottom || !view.data || view.data.navigationKey !== navigationKey || view.data.accountId !== accountId || markedFor.current === readKey) return;
    markedFor.current = readKey;
    inboxApi
      .markThreadRead(threadId)
      .then(() => {
        reloadUnread();
        reloadNotifications();
        onChanged();
      })
      .catch(() => {
        // Failing to record that something was read must not stop it being
        // read. The next open tries again.
        if (markedFor.current === readKey) markedFor.current = null;
      });
  }, [threadId, readKey, navigationKey, accountId, atBottom, view.data, reloadUnread, reloadNotifications, onChanged]);

  if ((!view.data || view.data.navigationKey !== navigationKey || view.data.accountId !== accountId) && (view.loading || view.error)) {
    return (
      <div className="inbox-conversation">
        <header className="inbox-conversation-head">
          <Link className="inbox-back" to="/inbox">← Inbox</Link>
        </header>
        {view.error
          ? <div className="error">We could not open that conversation.</div>
          : <p className="empty">Loading conversation…</p>}
      </div>
    );
  }
  if (!view.data || view.data.navigationKey !== navigationKey || view.data.accountId !== accountId) return null;

  const { thread, messages } = view.data;

  return (
    <div ref={container} className="inbox-conversation inbox-chat" style={{ height: chatHeight }}>
      <header className="inbox-conversation-head">
        <Link className="inbox-back" to={{ pathname: "/inbox", search: listSearch }}>
          ← Inbox
        </Link>
        <div>
          <h2>{thread.title}</h2>
          {thread.participants && <p className="small muted">{thread.participants}</p>}
        </div>
        {thread.openStudentId && (
          <Link
            className="notes-link"
            to={`/students/${encodeURIComponent(thread.openStudentId)}`}
          >
            Open student
          </Link>
        )}
      </header>

      <ConversationHistory key={`${accountId}:${navigationKey}`} threadId={threadId} latest={messages} onAtBottom={setAtBottom} sentVersion={nonce} />

      {thread.canReply ? (
        <Reply
          threadId={threadId}
          linkPicker={thread.authority === "staff" && (thread.kind === "staff_direct" || thread.kind === "student_family")}
          linkAudienceLabel={thread.kind === "student_family" ? "family" : "colleague"}
          lessonContextId={lessonContextId}
          onSent={() => {
            setAtBottom(true);
            setNonce((value) => value + 1);
            onChanged();
          }}
        />
      ) : (
        <p className="small muted inbox-no-reply">
          {thread.replyUnavailableReason === "announcement" || thread.replyPolicy === "none"
            ? "This is an announcement, so there is nothing to reply to."
            : thread.replyUnavailableReason === "no_recipients"
              ? "Nobody is currently available to receive a reply."
              : "You are no longer part of this conversation."}
        </p>
      )}
    </div>
  );
}

/**
 * Both routes render this. `threadId` comes from the path, so the list and the
 * open conversation are the same screen in two states rather than two screens.
 */
export function Inbox() {
  const { threadId } = useParams<{ threadId: string }>();
  const { me } = useAuth();
  const accountId = me?.user.id ?? "";
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = inboxFilter(searchParams.get("filter"));
  const page = inboxPage(searchParams.get("page"));
  const listSearch = inboxListSearch(filter, page);
  const historyAccountId = accountIdFromHistoryState(location.state);
  const selectionBelongsToAnotherAccount = Boolean(
    threadId && historyAccountId !== null && historyAccountId !== accountId,
  );
  const selected = selectionBelongsToAnotherAccount ? null : threadId ?? null;
  const lessonContextId = selected ? lessonIdFromHistoryState(location.state, accountId) : null;
  const [listNonce, setListNonce] = useState(0);
  const invalidateList = useCallback(() => setListNonce((value) => value + 1), []);
  const changePage = useCallback((nextPage: number, options?: { replace?: boolean }) => {
    setSearchParams(inboxListParams(filter, inboxPage(String(nextPage))), {
      replace: options?.replace === true,
    });
  }, [filter, setSearchParams]);
  const changeFilter = useCallback((nextFilter: InboxFilter) => {
    setSearchParams(inboxListParams(nextFilter, 1));
  }, [setSearchParams]);

  useEffect(() => {
    if (selectionBelongsToAnotherAccount) {
      navigate(`/inbox${listSearch}`, { replace: true, state: null });
      return;
    }
    if (location.search !== listSearch) {
      navigate(`${location.pathname}${listSearch}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [listSearch, location.pathname, location.search, location.state, navigate, selectionBelongsToAnotherAccount]);

  return (
    <section className={`inbox${selected ? " has-selection" : ""}`} aria-labelledby="inbox-heading">
      <div className="inbox-heading-row">
        <h1 id="inbox-heading" className="inbox-heading">Inbox</h1>
        <MessageAlerts key={accountId} accountId={accountId} />
      </div>
      <div className="inbox-layout">
        <ConversationList
          key={accountId}
          selectedId={selected}
          filter={filter}
          onFilter={changeFilter}
          page={page}
          onPage={changePage}
          nonce={listNonce}
          search={listSearch}
          accountId={accountId}
        />
        <div className="inbox-detail">
          {selected ? (
            <Conversation
              key={`${accountId}:${selected}`}
              threadId={selected}
              listSearch={listSearch}
              accountId={accountId}
              lessonContextId={lessonContextId}
              onChanged={invalidateList}
            />
          ) : (
            <p className="empty inbox-nothing-selected">Choose a conversation to read it.</p>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * The staff action from a student record: open this family's conversation and
 * go to it.
 *
 * Deliberately not a second composer. There was one publishing mechanism on
 * the student page and there is now one Inbox; keeping a form here as well
 * would be the parallel system this rollout exists to remove.
 */
export function MessageFamilyButton({
  studentId,
  studentName,
}: {
  studentId: string;
  studentName: string;
}) {
  const navigate = useNavigate();
  const { me } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function open() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { threadId } = await inboxApi.openStudentConversation(studentId);
      navigate(`/inbox/${encodeURIComponent(threadId)}`, {
        state: { inboxAccountId: me?.user.id ?? null },
      });
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "We could not open that conversation.",
      );
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="message-family-heading">
      <h2 id="message-family-heading">Message family</h2>
      <div className="panel">
        <p className="small muted">
          Opens your Inbox conversation with {studentName}&rsquo;s family. Everything you send
          and everything they reply stays in one place.
        </p>
        {error && <div className="error">{error}</div>}
        <div className="actions-end">
          <button type="button" onClick={() => void open()} disabled={busy}>
            {busy ? "Opening…" : "Open conversation"}
          </button>
        </div>
      </div>
    </section>
  );
}
